<script>
import { flip } from "svelte/animate";
import { dndzone } from "svelte-dnd-action";
import { clickoutside } from "@svelte-put/clickoutside";
import * as m from "@app/paraglide/messages.js";
import {
    editorState,
    saveEditorSettings,
    mergeTableColumnOrder,
} from "@app/store/editorStore.svelte.js";
import { getColumnLabels } from "./eventTableColumns.js";

let open = $state(false);
const flipDurationMs = 180;

let labels = $derived(getColumnLabels());

/** @type {{ id: string, label: string }[]} */
let items = $state([]);

function syncItemsFromStore() {
    const order = mergeTableColumnOrder(editorState.tableColumnOrder);
    items = order
        .filter((id) => id !== "number")
        .map((id) => ({ id, label: labels[id] || id }));
}

function toggleOpen() {
    if (!open) {
        syncItemsFromStore();
    }
    open = !open;
}

function close() {
    open = false;
}

/**
 * @param {CustomEvent} e
 */
function handleDnd(e) {
    items = e.detail.items;
    if (e.type === "finalize") {
        editorState.tableColumnOrder = mergeTableColumnOrder(
            items.map((item) => item.id),
        );
        saveEditorSettings();
    }
}

/**
 * @param {string} id
 * @param {boolean} visible
 */
function setVisibility(id, visible) {
    editorState.tableColumnVisibility = {
        ...editorState.tableColumnVisibility,
        [id]: visible,
        number: true,
    };
    saveEditorSettings();
}
</script>

<span
    class="column-settings"
    use:clickoutside
    onclickoutside={close}>
    <button
        type="button"
        class="btn btn-sm py-0 px-1 border-0 {open ? 'btn-primary' : 'btn-outline-secondary'}"
        onclick={toggleOpen}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={m.editor_table_columns_aria_label()}
        title={m.editor_table_columns_title()}>
        <i class="bi bi-layout-three-columns" aria-hidden="true"></i>
    </button>

    {#if open}
        <div
            class="column-settings-popover shadow border rounded bg-body p-2"
            role="dialog"
            aria-label={m.editor_table_columns_heading()}>
            <div class="fw-semibold small mb-2 px-1">
                {m.editor_table_columns_heading()}
            </div>
            <p class="text-muted small mb-2 px-1">
                {m.editor_table_columns_hint()}
            </p>

            <!-- Pinned # column -->
            <div class="column-row column-row-pinned px-1 py-1 mb-1">
                <span class="drag-placeholder" aria-hidden="true"></span>
                <input
                    type="checkbox"
                    class="form-check-input"
                    checked
                    disabled
                    aria-label={labels.number} />
                <span class="small text-truncate">{labels.number}</span>
                <span class="badge text-bg-secondary ms-auto">{m.editor_table_columns_pinned()}</span>
            </div>

            <div
                class="column-list"
                use:dndzone={{ items, flipDurationMs, dropTargetStyle: {} }}
                onconsider={handleDnd}
                onfinalize={handleDnd}>
                {#each items as item (item.id)}
                    <div
                        class="column-row px-1 py-1"
                        animate:flip={{ duration: flipDurationMs }}>
                        <span
                            class="drag-handle"
                            title={m.editor_table_columns_drag_handle()}
                            aria-hidden="true">
                            <i class="bi bi-grip-vertical"></i>
                        </span>
                        <input
                            type="checkbox"
                            class="form-check-input"
                            checked={!!editorState.tableColumnVisibility[item.id]}
                            onchange={(e) =>
                                setVisibility(item.id, e.currentTarget.checked)}
                            aria-label={item.label} />
                        <span class="small text-truncate">{item.label}</span>
                    </div>
                {/each}
            </div>
        </div>
    {/if}
</span>

<style>
.column-settings {
    position: relative;
    display: inline-block;
}
.column-settings-popover {
    position: absolute;
    bottom: calc(100% + 0.25rem);
    left: 0;
    z-index: 1060;
    width: 240px;
    max-height: min(360px, 50vh);
    overflow: auto;
    text-align: start;
}
.column-list {
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.column-row {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    border-radius: 0.25rem;
    background: transparent;
    cursor: grab;
}
.column-row:active {
    cursor: grabbing;
}
.column-row-pinned {
    cursor: default;
    opacity: 0.85;
}
.drag-handle,
.drag-placeholder {
    width: 1rem;
    display: inline-flex;
    justify-content: center;
    color: var(--bs-secondary-color, #6c757d);
}
.drag-placeholder {
    visibility: hidden;
}
</style>
